import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import "../styles/megaDropdown.css";

/**
 * Navbar flyout: a compact icon+title list on the left, and a detail preview
 * (icon+title+description) on the right that follows whichever list item is
 * currently hovered. Opens on hover or on caret click on desktop; on mobile
 * (no hover) it's a plain expandable list with no preview pane (see
 * megaDropdown.css).
 *
 * When `to` is given, the label itself is a real link to that category's main
 * page — clicking it navigates immediately, independent of the dropdown.
 * Categories with no single landing page (e.g. grouping several unrelated
 * pages) omit `to` and the whole trigger just toggles the flyout instead.
 *
 * @param {string} label
 * @param {string} [to] - the category's own page, if it has one
 * @param {{to: string, icon: object, title: string, description: string}[]} items
 * @param {() => void} [onNavigate]
 * @param {(open: boolean) => void} [onOpenChange]
 */
export default function MegaDropdown({
  label,
  to,
  items,
  onNavigate,
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const closeTimerRef = useRef(null);
  const { pathname } = useLocation();

  function setOpenState(next) {
    setOpen(next);
    onOpenChange?.(next);
  }

  useEffect(() => {
    setOpenState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpenState(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") setOpenState(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  function handleMouseEnter() {
    clearTimeout(closeTimerRef.current);
    setOpenState(true);
  }

  function handleMouseLeave() {
    closeTimerRef.current = setTimeout(() => setOpenState(false), 150);
  }

  function handleNavigate() {
    setOpenState(false);
    onNavigate?.();
  }

  const activeItem = items[activeIndex];

  return (
    <div
      className="mega-dropdown"
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="mega-dropdown-trigger">
        {to ? (
          <Link
            to={to}
            className="mega-dropdown-label"
            onClick={handleNavigate}
          >
            {label}
          </Link>
        ) : (
          <span className="mega-dropdown-label">{label}</span>
        )}
        <button
          type="button"
          className="mega-dropdown-caret"
          aria-expanded={open}
          aria-label={`${label} almenü`}
          onClick={() => setOpenState(!open)}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`dropdown-arrow ${open ? "is-open" : ""}`}
          />
        </button>
      </div>

      <div
        className={`mega-dropdown-panel ${open ? "is-open" : ""}`}
        role="menu"
        aria-label={label}
      >
        <div className="mega-list">
          {items.map((item, index) => (
            <Link
              key={item.title}
              to={item.to}
              className={`mega-list-item ${index === activeIndex ? "is-active" : ""}`}
              role="menuitem"
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onClick={handleNavigate}
            >
              <span className="mega-list-icon">
                <FontAwesomeIcon icon={item.icon} fixedWidth />
              </span>
              <span className="mega-list-title">{item.title}</span>
            </Link>
          ))}
        </div>

        <Link
          to={activeItem.to}
          className="mega-preview"
          onClick={handleNavigate}
        >
          <span className="mega-preview-desc">{activeItem.description}</span>
        </Link>
      </div>
    </div>
  );
}
